// frontend/src/components/Templates/TemplatePicker.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Input, List, Tag, Button, Space, Typography } from 'antd';
import { templateApi } from '../../services/api';
import type { Template } from '../../types';

const { Text } = Typography;

export function TemplatePicker(props: {
  open: boolean;
  onClose: () => void;
  onApply: (t: Template) => void;
  canManage?: boolean;
  onDelete?: (id: number) => void;
}) {
  const { open, onClose, onApply, canManage, onDelete } = props;
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    try { const res = await templateApi.getAll({ search: q || undefined }); setItems(res.data); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(search); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => load(search), 300);
    return () => clearTimeout(h);
  }, [search, open, load]);

  const summarize = (t: Template) => t.type === 'part'
    ? `${t.part_number || ''}`
    : `${(t.payload as any).labor_hours ?? 0}h labor · ${((t.payload as any).parts || []).length} part(s)`;

  return (
    <Modal open={open} onCancel={onClose} centered footer={null} title="Templates" width={640}>
      <Input.Search placeholder="Search by name or part number…" value={search} onChange={e => setSearch(e.target.value)} allowClear style={{ marginBottom: 12 }} />
      <List
        loading={loading}
        dataSource={items}
        locale={{ emptyText: 'No templates yet' }}
        renderItem={(t) => (
          <List.Item
            actions={[
              <Button key="apply" type="primary" size="small" onClick={() => onApply(t)}>Apply</Button>,
              ...(canManage && onDelete ? [<Button key="del" size="small" danger onClick={() => onDelete(t.id)}>Delete</Button>] : []),
            ]}
          >
            <List.Item.Meta
              title={<Space><Tag color={t.type === 'line' ? 'blue' : 'gold'}>{t.type === 'line' ? 'Line' : 'Part'}</Tag>{t.name}</Space>}
              description={<Text type="secondary">{summarize(t)}</Text>}
            />
          </List.Item>
        )}
      />
    </Modal>
  );
}
